"use client"

import { useEffect, useRef } from "react"
import { cn } from "@/lib/utils"
import { MessageActions } from "@/components/ui/message-actions"
import type { ChatSettings } from "@/contexts/ChatPanelContext"
import type { Message } from "@/types/chat.types"

interface AppChatConversationProps {
  messages: Message[]
  settings: ChatSettings
  isLoading: boolean
  variant?: "panel" | "page"
  emptyState?: React.ReactNode
  className?: string
}

export function AppChatConversation({
  messages,
  settings,
  isLoading,
  variant = "panel",
  emptyState,
  className,
}: AppChatConversationProps) {
  const endRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [isLoading, messages])

  if (messages.length === 0 && !isLoading) {
    return (
      <div className={cn("flex min-h-0 flex-1 items-center justify-center px-4 py-4", className)}>
        {emptyState}
      </div>
    )
  }

  const fontSizeClass =
    settings.fontSize === "small" ? "text-xs"
      : settings.fontSize === "large" ? "text-base"
        : "text-sm"

  return (
    <div className={cn("min-h-0 flex-1 overflow-y-auto", className)}>
      <div className={cn(
        variant === "page"
          ? "mx-auto max-w-2xl space-y-6 px-6 py-6"
          : "space-y-4 px-4 py-4",
      )}>
        {messages.map((message) => {
          const isUser = message.role === "user"

          return (
            <div
              key={message.id}
              className={cn(
                "flex gap-3",
                variant === "panel" && isUser ? "justify-end" : "justify-start",
              )}
            >
              {!isUser && (
                <div className={cn(
                  "mt-0.5 flex shrink-0 items-center justify-center bg-muted",
                  variant === "page" ? "size-8 rounded-lg" : "size-7 rounded-md",
                )}>
                  <img
                    src="/img/logo.png"
                    alt="AI"
                    className="size-4 object-contain opacity-70"
                  />
                </div>
              )}
              <div className={cn(
                "min-w-0",
                variant === "page" ? "flex-1" : "max-w-[85%]",
              )}>
                {(settings.showTimestamps || settings.showModelInfo || settings.showScopeBadge) && (
                  <div className={cn(
                    "mb-1 flex flex-wrap items-center gap-1.5 px-1",
                    variant === "panel" && isUser ? "justify-end" : "justify-start",
                  )}>
                    {settings.showScopeBadge && message.scope && (
                      <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                        {message.scope}
                      </span>
                    )}
                    {settings.showModelInfo && !isUser && (
                      <span className="text-[10px] text-muted-foreground/70">
                        claude-sonnet-4-6
                      </span>
                    )}
                    {settings.showTimestamps && (
                      <span className="text-[10px] text-muted-foreground/60">
                        {message.timestamp.toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    )}
                  </div>
                )}

                <div
                  className={cn(
                    "rounded-2xl px-3.5 py-2 w-fit",
                    isUser
                      ? "bg-foreground text-background"
                      : "bg-muted text-foreground",
                    variant === "page" && !isUser && "",
                    variant === "page" && isUser && "ml-auto",
                  )}
                >
                  <p className={cn(fontSizeClass, "whitespace-pre-wrap")}>{message.content}</p>
                </div>

                {!isUser && (settings.showTokenUsage || settings.showConfidence) && (
                  <div className="mt-1 flex items-center gap-2 px-1">
                    {settings.showTokenUsage && (
                      <span className="font-mono text-[10px] text-muted-foreground/60">
                        ~{Math.floor(message.content.length / 4)} tokens
                      </span>
                    )}
                    {settings.showConfidence && (
                      <span className="flex items-center gap-1 text-[10px] text-muted-foreground/60">
                        <span className="inline-block size-1.5 rounded-full bg-emerald-500" />
                        high confidence
                      </span>
                    )}
                  </div>
                )}

                {settings.showContextSent && isUser && (message.contextItems?.length ?? 0) > 0 && (
                  <div className="mt-1 flex flex-wrap items-center gap-1 px-1">
                    <span className="text-[10px] text-muted-foreground/50">ctx:</span>
                    {message.contextItems?.map((item) => (
                      <span
                        key={item.id}
                        className="rounded bg-muted px-1 py-0.5 text-[10px] text-muted-foreground/60"
                      >
                        {item.label}
                      </span>
                    ))}
                  </div>
                )}

                {message.actions && message.actions.length > 0 && (
                  <MessageActions actions={message.actions} messageRole={message.role} className="mt-2" />
                )}
              </div>
              {isUser && variant === "panel" && (
                <div className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-primary/10">
                  <span className="text-[10px] font-medium">You</span>
                </div>
              )}
            </div>
          )
        })}

        {isLoading && (
          <div className="flex gap-3 justify-start">
            <div className={cn(
              "mt-0.5 flex shrink-0 items-center justify-center rounded-md bg-muted",
              variant === "page" ? "size-8 rounded-lg" : "size-7",
            )}>
              <img
                src="/img/logo.png"
                alt="AI"
                className="size-4 object-contain opacity-70"
              />
            </div>
            <div className="rounded-2xl bg-muted px-3.5 py-2.5">
              <div className="flex gap-1">
                <span className="size-1.5 animate-bounce rounded-full bg-foreground/40" style={{ animationDelay: "0ms" }} />
                <span className="size-1.5 animate-bounce rounded-full bg-foreground/40" style={{ animationDelay: "150ms" }} />
                <span className="size-1.5 animate-bounce rounded-full bg-foreground/40" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          </div>
        )}

        <div ref={endRef} />
      </div>
    </div>
  )
}
